/**
 * DrawnSignatureCapture — HTML5 canvas + Pointer Events primitive.
 *
 * Bill 2026-05-10 — Phase 1 (v0.14.17 bundle).
 *
 * Q1 doc-role tier: required for signature cards, banking resolutions, BO
 * certifications, trust acceptance certificates, and any doc destined for
 * an external system (notarization, court records, IRS forms, JHA Core
 * Director signature-card store).
 *
 * Pointer Events API is the right primitive — covers mouse, touch, and
 * stylus through one event surface. Pen pressure is captured per-point
 * when the device reports it (Apple Pencil, Surface Pen, Wacom).
 *
 * Mobile-fallback hint: when `isTouchOnly=false` and the device has no
 * touch capability, we surface a banner suggesting the customer switch to
 * a phone. The QR code itself is a Wave-2 wiring deliverable (depends on
 * the cross-device hand-off JWT route) — for now we render a text link
 * placeholder and document the dependency.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { SignatureCaptureResult } from '@retail-ops/shared-types';

// ── Props ──────────────────────────────────────────────────────────────────

export interface DrawnSignatureCaptureProps {
  signerName: string;
  documentTitle: string;
  onCapture: (result: SignatureCaptureResult) => void;
  onCancel: () => void;
  disabled?: boolean;
  /**
   * Q1 mobile-fallback flag — set true when the surface is already a
   * touch-only device (the customer was handed off from desktop). When
   * true we hide the mouse-not-recommended banner.
   */
  isTouchOnly?: boolean;
}

// ── Geometry ───────────────────────────────────────────────────────────────

interface StrokePoint {
  x: number;
  y: number;
  t: number;
  pressure: number;
}

interface Stroke {
  points: StrokePoint[];
}

// ── Styles ─────────────────────────────────────────────────────────────────

const cardS: CSSProperties = {
  background: '#fff',
  color: '#0f172a',
  borderRadius: 10,
  maxWidth: 760,
  width: '100%',
  padding: 24,
  boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  fontFamily: "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif",
};

const headingS: CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: '#1a406a',
};

const subHeadS: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: '#475569',
};

const fallbackBannerS: CSSProperties = {
  background: '#fef3c7',
  color: '#7c4a03',
  border: '1px solid #fbbf24',
  borderRadius: 8,
  padding: '12px 14px',
  fontSize: 13,
  lineHeight: 1.4,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const canvasWrapS: CSSProperties = {
  width: '100%',
  background: '#f8fafc',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  position: 'relative',
  overflow: 'hidden',
};

const canvasS: CSSProperties = {
  display: 'block',
  width: '100%',
  height: 200,
  touchAction: 'none',
  cursor: 'crosshair',
  background: '#ffffff',
};

const baselineS: CSSProperties = {
  position: 'absolute',
  left: 24,
  right: 24,
  bottom: 36,
  borderTop: '1px dashed #cbd5e1',
  pointerEvents: 'none',
};

const baselineXS: CSSProperties = {
  position: 'absolute',
  left: 24,
  bottom: 28,
  color: '#94a3b8',
  fontSize: 18,
  pointerEvents: 'none',
};

const baselineHintS: CSSProperties = {
  position: 'absolute',
  right: 24,
  bottom: 30,
  color: '#94a3b8',
  fontSize: 11,
  pointerEvents: 'none',
  fontStyle: 'italic',
};

const controlsRowS: CSSProperties = {
  display: 'flex',
  gap: 10,
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 4,
};

const ghostBtnS: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#1f2937',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

const primaryBtnS: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 6,
  border: '1px solid #386732',
  background: '#386732',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const disabledBtnS: CSSProperties = {
  ...primaryBtnS,
  background: '#9ca3af',
  border: '1px solid #9ca3af',
  cursor: 'not-allowed',
};

// ── Helpers ────────────────────────────────────────────────────────────────

function detectHasTouch(): boolean {
  if (typeof window === 'undefined') return false;
  if ('ontouchstart' in window) return true;
  if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) {
    return true;
  }
  return false;
}

function strokeToPathD(stroke: Stroke): string {
  if (stroke.points.length === 0) return '';
  const [first, ...rest] = stroke.points;
  if (!first) return '';
  const parts = [`M ${first.x.toFixed(1)} ${first.y.toFixed(1)}`];
  for (const p of rest) {
    parts.push(`L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`);
  }
  return parts.join(' ');
}

function strokesToSvgPath(strokes: Stroke[]): string {
  return strokes
    .map(strokeToPathD)
    .filter((d) => d.length > 0)
    .join(' ');
}

// ── Component ──────────────────────────────────────────────────────────────

const CANVAS_WIDTH = 760;
const CANVAS_HEIGHT = 200;

export function DrawnSignatureCapture(
  props: DrawnSignatureCaptureProps
): JSX.Element {
  const {
    signerName,
    documentTitle,
    onCapture,
    onCancel,
    disabled,
    isTouchOnly,
  } = props;

  const intentTimestampRef = useRef<string>(new Date().toISOString());
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const currentStrokeRef = useRef<Stroke | null>(null);
  const isDrawingRef = useRef<boolean>(false);

  const [hasInkBeenDrawn, setHasInkBeenDrawn] = useState<boolean>(false);
  const [, forceRender] = useState<number>(0);

  const hasTouch = useMemo(() => detectHasTouch(), []);
  const showFallback = !isTouchOnly && !hasTouch;

  // ── Canvas setup (HiDPI-aware) ─────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = CANVAS_WIDTH * dpr;
    canvas.height = CANVAS_HEIGHT * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1a406a';
    ctx.lineWidth = 2.2;
  }, []);

  function getLocalPoint(
    e: ReactPointerEvent<HTMLCanvasElement>
  ): { x: number; y: number } {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    // Scale the client coords back into the canvas's logical coordinate
    // space (we keep CSS at CANVAS_WIDTH x CANVAS_HEIGHT via the wrapper).
    const x = ((e.clientX - rect.left) * CANVAS_WIDTH) / rect.width;
    const y = ((e.clientY - rect.top) * CANVAS_HEIGHT) / rect.height;
    return { x, y };
  }

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (disabled) return;
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        /* not all browsers expose setPointerCapture cleanly */
      }
      isDrawingRef.current = true;
      const { x, y } = getLocalPoint(e);
      const stroke: Stroke = {
        points: [{ x, y, t: Date.now(), pressure: e.pressure || 0.5 }],
      };
      currentStrokeRef.current = stroke;
      strokesRef.current = [...strokesRef.current, stroke];

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(x, y);
      }
    },
    [disabled]
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current || disabled) return;
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const { x, y } = getLocalPoint(e);
      const stroke = currentStrokeRef.current;
      if (!stroke) return;
      stroke.points.push({
        x,
        y,
        t: Date.now(),
        pressure: e.pressure || 0.5,
      });
      // Pressure-modulated line width (very subtle so the stroke still
      // looks like a signature, not a pen-test).
      ctx.lineWidth = 1.6 + (e.pressure || 0.5) * 1.6;
      ctx.lineTo(x, y);
      ctx.stroke();
      if (!hasInkBeenDrawn) {
        setHasInkBeenDrawn(true);
      }
    },
    [disabled, hasInkBeenDrawn]
  );

  const handlePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current) return;
      e.preventDefault();
      isDrawingRef.current = false;
      currentStrokeRef.current = null;
      // Force a render so the Confirm button picks up enable-state when
      // hasInkBeenDrawn flipped in handlePointerMove.
      forceRender((n) => n + 1);
    },
    []
  );

  function handleClear(): void {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, CANVAS_WIDTH * dpr, CANVAS_HEIGHT * dpr);
    ctx.restore();
    strokesRef.current = [];
    currentStrokeRef.current = null;
    setHasInkBeenDrawn(false);
  }

  function handleConfirm(): void {
    if (!hasInkBeenDrawn || disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Rasterise at the canvas's actual backing-store resolution (HiDPI
    // already baked in by the setup effect). `toDataURL` returns a `data:`
    // URI — strip the prefix so the server gets raw base64.
    const pngDataUrl = canvas.toDataURL('image/png');
    const pngBase64 = pngDataUrl.replace(/^data:image\/png;base64,/, '');
    const svgPath = strokesToSvgPath(strokesRef.current);
    const now = new Date().toISOString();
    const result: SignatureCaptureResult = {
      mode: 'drawn',
      svgPath,
      pngBase64,
      attestationTimestamp: now,
      intentTimestamp: intentTimestampRef.current,
      signerViewportSize: `${window.innerWidth}x${window.innerHeight}`,
      signerUserAgent: navigator.userAgent,
    };
    onCapture(result);
  }

  return (
    <div role="dialog" aria-label="Sign with your finger or stylus" style={cardS}>
      <h2 style={headingS}>Sign with your finger or stylus</h2>
      <p style={subHeadS}>
        Sign on the line below for <strong>{documentTitle}</strong>. Tap{' '}
        <em>Clear</em> if you want to start over. Your signature will look the
        same on the final document as it does in this box.
      </p>

      {showFallback && (
        <div style={fallbackBannerS} role="status">
          <strong>Mouse-drawn signatures don&apos;t look right.</strong>
          <span>
            For a cleaner result, open this on your phone and sign with your
            finger or stylus. Ask the FSR for a hand-off link, or scan the QR
            code that will appear here (cross-device hand-off ships in Wave 2 —
            TBD).
          </span>
        </div>
      )}

      <div style={canvasWrapS}>
        <canvas
          ref={canvasRef}
          style={canvasS}
          aria-label={`Signature canvas for ${signerName}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
        <div style={baselineS} />
        <span style={baselineXS}>×</span>
        <span style={baselineHintS}>Sign above the line</span>
      </div>

      <div style={controlsRowS}>
        <button
          type="button"
          style={ghostBtnS}
          onClick={handleClear}
          disabled={disabled}
        >
          Clear
        </button>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            style={ghostBtnS}
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </button>
          <button
            type="button"
            style={
              hasInkBeenDrawn && !disabled ? primaryBtnS : disabledBtnS
            }
            disabled={!hasInkBeenDrawn || disabled}
            onClick={handleConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default DrawnSignatureCapture;
